import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckIcon, ArrowRightIcon } from '@heroicons/react/24/outline';

const PricingPage: React.FC = () => {
  const [isYearly, setIsYearly] = useState(false);

  const plans = [
    {
      name: 'Starter + Free Trial',
      monthlyPrice: 49,
      yearlyPrice: 39,
      description: 'Essential Fashion & Beauty Intelligence',
      valueProposition: 'Core trend insights for individuals and small teams',
      features: [
        '200 API calls/month',
        '3 data sources (Reddit, YouTube, Google Trends)',
        'Fashion + Beauty trends only',
        'Basic engagement & viral potential metrics',
        '3 smart alerts (email)',
        'Bi-weekly trend digest',
        '25 trends/month CSV export',
      ],
      limitations: [],
      cta: 'Start Free Trial',
      popular: false,
      hasTrial: true,
      trialDays: 7,
    },
    {
      name: 'Growth',
      monthlyPrice: 89,
      yearlyPrice: 69,
      description: 'Enhanced Analytics for Growing Businesses',
      valueProposition: 'Advanced insights for small agencies and growing creators',
      features: [
        '500 API calls/month',
        '4+ enhanced data sources',
        'Momentum classification & basic demographics',
        '7 smart alerts + basic webhooks',
        'Weekly reports & trend comparison',
        '7-day trend predictions',
        'Unlimited CSV export',
      ],
      limitations: [],
      cta: 'Get Started',
      popular: true,
      hasTrial: false,
      trialDays: 0,
    },
    {
      name: 'Professional',
      monthlyPrice: 129,
      yearlyPrice: 99,
      description: 'Advanced Predictions & Forecasting',
      valueProposition: 'Professional-grade analytics for agencies and brands',
      features: [
        '1,000 API calls/month',
        '5+ enhanced data sources',
        'Full demographics & competitor analysis',
        '15 smart alerts + advanced webhooks',
        'Daily briefings & market insights',
        '14-day advanced forecasting',
        'Unlimited CSV/JSON/API export',
      ],
      limitations: [],
      cta: 'Get Started',
      popular: false,
      hasTrial: false,
      trialDays: 0,
    },
    {
      name: 'Business',
      monthlyPrice: 299,
      yearlyPrice: 224,
      description: 'Complete Fashion Intelligence Platform',
      valueProposition: 'Full ML suite with custom branding for established brands',
      features: [
        '5,000 API calls/month',
        'All data sources + premium coverage',
        'Full ML prediction suite',
        'Unlimited alerts + real-time notifications',
        'White-label reports & custom dashboards',
        '30-day forecasting + seasonal analysis',
        'Market sizing & competitor analysis',
      ],
      limitations: [],
      cta: 'Get Started',
      popular: false,
      hasTrial: false,
      trialDays: 0,
    },

  ];

  const additionalServices = [
    {
      name: 'Custom Reports',
      price: '£500-2000',
      description: 'Tailored trend analysis reports for your specific needs',
      timeframe: 'per report',
    },
    {
      name: 'Consulting',
      price: '£150-300',
      description: 'Expert trend strategy consulting and guidance',
      timeframe: 'per hour',
    },
    {
      name: 'Training',
      price: '£2000-5000',
      description: 'Team workshops and training sessions',
      timeframe: 'per workshop',
    },
    {
      name: 'Data Exports',
      price: '£100-500',
      description: 'Historical trend data exports',
      timeframe: 'per export',
    },
    {
      name: 'API Access',
      price: '£50-200',
      description: 'Additional API access for developers',
      timeframe: 'per month',
    },
  ];

  const faqs = [
    {
      question: 'What\'s included in the 7-day free trial?',
      answer: 'All paid plans come with a 7-day free trial that includes full access to all features of your chosen plan. No credit card required to start your trial.',
    },
    {
      question: 'Can I change my plan at any time?',
      answer: 'Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately, and we\'ll prorate any charges accordingly.',
    },
    {
      question: 'What kind of support do you offer?',
      answer: 'Starter plan users get email support. Professional plan users get priority email and chat support. Business plan users get phone support and a dedicated customer success manager.',
    },
    {
      question: 'How often is the trend data updated?',
      answer: 'Our trend data is updated in real-time, with new trends being detected and analysed continuously throughout the day.',
    },
    {
      question: 'What\'s the difference between the plans?',
      answer: 'Starter focuses on essential fashion & beauty trend intelligence with basic analytics. Professional adds advanced predictions and forecasting capabilities. Business provides the complete platform with ML insights, white-label reports, and dedicated support.',
    },
    {
      question: 'Do you offer custom pricing for large organizations?',
      answer: 'Yes, we offer custom enterprise solutions for large organizations with specific requirements, including custom integrations, dedicated support, and tailored pricing.',
    },
  ];

  const getPrice = (plan: typeof plans[0]) => {
    return isYearly ? plan.yearlyPrice : plan.monthlyPrice;
  };

  const getSavings = (plan: typeof plans[0]) => {
    if (plan.monthlyPrice === 0) return 0;
    return Math.round(((plan.monthlyPrice * 12 - plan.yearlyPrice * 12) / (plan.monthlyPrice * 12)) * 100);
  };

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-purple-50 to-indigo-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
          >
            Value-driven pricing
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-xl text-gray-600 max-w-2xl mx-auto mb-8"
          >
            Choose the plan that delivers the most value for your needs. All plans include a 7-day free trial.
          </motion.p>
          
          {/* Billing Toggle */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="flex flex-col items-center h-16"
          >
            <div className="flex items-center space-x-4 mb-2">
              <span className={`text-sm font-medium ${!isYearly ? 'text-gray-900' : 'text-gray-500'}`}>
                Monthly
              </span>
              <button
                onClick={() => setIsYearly(!isYearly)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  isYearly ? 'bg-purple-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    isYearly ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className={`text-sm font-medium ${isYearly ? 'text-gray-900' : 'text-gray-500'}`}>
                Yearly
              </span>
            </div>
            <div className="h-6 flex items-center">
              {isYearly && (
                <span className="text-sm text-green-600">Save up to 25% with yearly billing</span>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pricing Plans */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 lg:gap-8">
            {plans.map((plan, index) => (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`bg-gradient-to-br from-white to-gray-50 rounded-xl shadow-lg border-2 p-6 pt-8 relative flex flex-col h-full transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${
                  plan.popular ? 'border-purple-500 ring-2 ring-purple-200' : 'border-gray-200'
                } ${plan.popular ? 'scale-105' : ''}`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10 min-w-max">
                    <span className="bg-gradient-to-r from-purple-500 to-purple-600 text-white px-4 py-1.5 rounded-full text-sm font-bold shadow-lg whitespace-nowrap">
                      Most Popular
                    </span>
                  </div>
                )}
                
                <div className="text-center mb-6 flex-shrink-0">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                  <p className="text-sm text-purple-600 font-medium mb-2">{plan.description}</p>
                  <div className="mb-3 h-16 flex flex-col justify-center">
                    {plan.hasTrial ? (
                      <div>
                        <span className="text-2xl font-bold text-green-600">Free Trial</span>
                        <div className="text-sm text-gray-500">Then £{plan.monthlyPrice}/month</div>
                      </div>
                    ) : (
                      <div>
                        <span className="text-3xl font-bold text-purple-600">
                          £{getPrice(plan)}
                        </span>
                        <span className="text-gray-600">/month</span>
                      </div>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mb-3">{plan.valueProposition}</p>
                  <div className="h-8 flex items-center justify-center mb-3">
                    {plan.hasTrial ? (
                      <div className="inline-flex items-center px-3 py-1.5 bg-green-50 border border-green-200 rounded-full text-sm font-medium text-green-700">
                        7-day free trial
                      </div>
                    ) : (
                      <div className="inline-flex items-center px-3 py-1.5 bg-purple-50 border border-purple-200 rounded-full text-sm font-medium text-purple-700">
                        Full features from day 1
                      </div>
                    )}
                  </div>
                  {isYearly && (
                    <div className="text-sm text-green-600">
                      Save {getSavings(plan)}% with yearly billing
                    </div>
                  )}
                </div>

                <ul className="space-y-3 mb-6 text-sm flex-grow">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start">
                      <CheckIcon className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-auto flex justify-center">
                  <Link
                    to={plan.name === 'Enterprise' ? '/contact?type=custom' : `/signup?plan=${plan.name.toLowerCase().replace(' + free trial', '')}&billingCycle=${isYearly ? 'yearly' : 'monthly'}`}
                    className={`w-full py-3 px-4 rounded-lg font-medium text-center transition-colors text-sm ${
                      plan.popular
                        ? 'bg-gradient-to-r from-purple-600 to-purple-700 text-white hover:from-purple-700 hover:to-purple-800 shadow-lg'
                        : 'bg-gradient-to-r from-gray-100 to-gray-200 text-gray-900 hover:from-gray-200 hover:to-gray-300 shadow-md'
                    }`}
                  >
                    {plan.cta}
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Additional Services
            </h2>
            <p className="text-xl text-gray-600">
              Custom solutions and professional services for your specific needs
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {additionalServices.map((service, index) => (
              <motion.div
                key={service.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-lg p-6 shadow-sm border border-gray-200"
              >
                <div className="text-center">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{service.name}</h3>
                  <div className="text-2xl font-bold text-purple-600 mb-2">
                    {service.price}
                  </div>
                  <div className="text-sm text-gray-500 mb-3">{service.timeframe}</div>
                  <p className="text-sm text-gray-600">{service.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-center mt-12"
          >
            <Link
              to="/services"
              className="bg-purple-600 text-white px-8 py-3 rounded-lg font-medium hover:bg-purple-700 transition-colors inline-flex items-center"
            >
              View All Services
              <ArrowRightIcon className="ml-2 h-5 w-5" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need to know about our pricing and plans
            </p>
          </motion.div>
          
          <div className="space-y-8">
            {faqs.map((faq, index) => (
              <motion.div
                key={faq.question}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-lg p-6 shadow-sm border border-gray-200"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  {faq.question}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {faq.answer}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Custom Solutions CTA */}
      <section className="py-20 bg-gradient-to-br from-purple-50 to-indigo-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Need a custom solution?
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              We offer custom enterprise solutions for large organizations with specific requirements, including custom integrations, dedicated support, and tailored pricing.
            </p>
            <Link
              to="/contact?type=custom"
              className="bg-purple-600 text-white px-8 py-3 rounded-lg font-medium hover:bg-purple-700 transition-colors inline-block"
            >
              Contact Sales Team
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PricingPage; 